/**
 * BookingAvailabilityGateway の Supabase 実装。
 *
 * - 空き枠判定（check_slot/check_resource）は重複予約の有無＝SDK の count クエリで表現する
 *   （旧 RPC の NOT EXISTS を置換。集計でないため過剰取得にならず、RLS でテナント保護される。方針B改訂）。
 * - get_available_slots はサーバ側スタブ（現状空返し）のため RPC のまま。
 */
import { supabase } from "@/integrations/supabase/client";
import { repository } from "@/infrastructure/repositories";
import type { Condition } from "@/domain/repositoryInterface/Repository";
import type {
  AvailableSlot,
  BookingAvailabilityGateway,
} from "@/domain/repositoryInterface/BookingAvailabilityGateway";

export class SupabaseBookingAvailabilityGateway implements BookingAvailabilityGateway {
  async checkSlot(params: {
    shopId: string;
    staffId: string;
    startTime: string;
    endTime: string;
    excludeBookingId?: string;
  }): Promise<boolean> {
    return this.hasNoOverlap(
      params.shopId,
      { column: "staff_id", op: "eq", value: params.staffId },
      params.startTime,
      params.endTime,
      params.excludeBookingId,
    );
  }

  async checkResource(params: {
    shopId: string;
    resourceId: string;
    startTime: string;
    endTime: string;
    excludeBookingId?: string;
  }): Promise<boolean> {
    return this.hasNoOverlap(
      params.shopId,
      { column: "resource_id", op: "eq", value: params.resourceId },
      params.startTime,
      params.endTime,
      params.excludeBookingId,
    );
  }

  /**
   * 指定枠と重複する有効予約が無ければ true（旧 check_*_availability RPC の NOT EXISTS 相当）。
   * 重複条件: start_time < endTime AND end_time > startTime、status は cancelled/no_show を除外。
   */
  private async hasNoOverlap(
    shopId: string,
    target: Condition,
    startTime: string,
    endTime: string,
    excludeBookingId?: string,
  ): Promise<boolean> {
    const where: Condition[] = [
      { column: "shop_id", op: "eq", value: shopId },
      target,
      { column: "deleted_at", op: "isNull" },
      { column: "status", op: "notIn", value: ["cancelled", "no_show"] },
      { column: "start_time", op: "lt", value: endTime },
      { column: "end_time", op: "gt", value: startTime },
    ];
    if (excludeBookingId) {
      where.push({ column: "id", op: "neq", value: excludeBookingId });
    }
    const { count } = await repository("bookings").findMany({
      select: "id",
      where,
      withCount: true,
      page: { index: 0, size: 1 },
    });
    return (count ?? 0) === 0;
  }

  async availableSlots(params: {
    shopId: string;
    menuId: string;
    date: string;
  }): Promise<AvailableSlot[]> {
    const { data, error } = await supabase.rpc("get_available_slots", {
      p_shop_id: params.shopId,
      p_menu_id: params.menuId,
      p_date: params.date,
    });
    if (error) throw error;
    return (data as AvailableSlot[]) ?? [];
  }
}
